import forge from "node-forge";
import { v4, Version4Options } from "uuid";
import { CreditType } from "./types";

// Format Units utility function (simplified version)
export const formatUnits = (value: bigint, decimals: number): string => {
  const divisor = BigInt(10 ** decimals);
  const quotient = value / divisor;
  const remainder = value % divisor;

  const formattedRemainder = remainder.toString().padStart(decimals, "0");
  return `${quotient.toString()}.${formattedRemainder}`;
};

// Helper function for formatting credit limits
export const formatAvailableLimit = (
  availableLimit: number,
  creditType: CreditType
): number => {
  const decimals =
    creditType === "aave-v3" || creditType === "hypurrFi" ? 6 : 18;
  const formatted = Number(formatUnits(BigInt(availableLimit), decimals));
  return Number(formatted.toFixed(4));
};

// Mapper function to convert Euler metadata to include suppliedAssets
export function mapEulerMetadataToSuppliedAssets(metadata: any) {
  if (!Array.isArray(metadata?.vaults)) return metadata;

  const suppliedAssets = metadata.vaults.map((vault: any) => {
    const formattedAsset = Number(formatUnits(BigInt(vault.assets || 0), 18));

    console.log({ formattedAsset });
    return {
      UsageAsCollateralEnabledOnUser: true, // Assume true for Euler
      amount: formattedAsset,
      amountUSD: 0, // No USD value provided, set to 0 or calculate if possible
      collateralAmountBase: String(formattedAsset),
      collateralAmountBig: formattedAsset,
      supplyRate: 0, // Not provided by Euler, set to 0 or fetch if available
      underlyingAsset: vault.vault || "",
    };
  });

  const suppliedValue = suppliedAssets.reduce(
    (sum: number, asset: any) => sum + asset.amountUSD,
    0
  );

  return {
    ...metadata,
    suppliedAssets,
    suppliedValue,
  };
}

export const waitFor = async (ms = 1500) =>
  await new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Encrypts a PIN using AES-GCM with a session key provided by Rain.
 *
 * @param pin - The raw numeric PIN (e.g. "1234")
 * @param sessionKey - Hex-encoded session key from Rain
 * @returns An object containing base64-encoded IV and encrypted data (with tag)
 */
export function encryptPinWithSessionKey(
  pin: string,
  sessionKey: string
): {
  iv: string;
  data: string;
} {
  // Step 1: Format PIN as `2{lenInHex}{PIN}{F-padding}`
  const hexLength = pin.length.toString(16);
  const formattedPin = `2${hexLength}${pin}${"F".repeat(14 - pin.length)}`;

  // Step 2: Generate 16-byte IV
  const ivBytes = forge.random.getBytesSync(16);
  const encodedIV = forge.util.encode64(ivBytes);

  // Step 3: Convert sessionKey to bytes
  const keyBytes = forge.util.hexToBytes(sessionKey);

  // Step 4: Create AES-GCM cipher
  const cipher = forge.cipher.createCipher("AES-GCM", keyBytes);
  cipher.start({ iv: ivBytes });

  // Step 5: Encrypt the formatted PIN
  cipher.update(forge.util.createBuffer(formattedPin));
  cipher.finish();

  // Step 6: Concatenate ciphertext + tag, then base64 encode
  const encrypted = cipher.output.getBytes() + cipher.mode.tag.getBytes();
  const encodedEncrypted = forge.util.encode64(encrypted);

  return {
    iv: encodedIV,
    data: encodedEncrypted,
  };
}

export const generateUUID = (options?: Version4Options) => {
  return v4(options);
};

/**
 * Returns the appropriate cardSolverConfig object for a given creditType.
 * Used to construct the payload for card creation.
 *
 * @param creditType - The type of credit ("aave-v3", "hypurrFi", or others)
 * @returns The cardSolverConfig object for the specified creditType
 */
export function getCardSolverConfig(
  creditType: string
): Record<string, string> {
  if (creditType === "aave-v3") {
    return {
      borrowTokenAddress: "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913",
    };
  } else if (creditType === "hypurrFi") {
    return {
      borrowTokenAddress: "0xb8ce59fc3717ada4c02eadf9682a9e934f625ebb",
    };
  } else {
    return {
      borrowVaultAddress: "0x0A1a3b5f2041F33522C4efc754a7D096f880eE16",
    };
  }
}
